import { useEffect, useState, type ReactNode } from 'react';
import {
  Eye,
  Film,
  GalleryHorizontal,
  FileText,
  Heart,
  Image as ImageIcon,
  Layers,
  Megaphone,
  MessageCircle,
  Music,
  Share2,
  type LucideIcon,
} from 'lucide-react';
import { ContentKind } from '@mg/shared';
import type { Post } from '@mg/shared';
import { cn } from '../../lib/cn';
import { Badge } from '../../components/ui/Badge';
import {
  displayStateLabel,
  displayStateTone,
  formatDateTime,
  formatMetric,
  metricsPending,
  type PostDisplayState,
} from '../../lib/display';
import { useThumbnailSrc } from '../../lib/useProxyImage';
import { PLATFORM_META } from '../social/platforms';
import {
  ON_APPROVAL_LABEL,
  RESCHEDULE_LABEL,
  UNSCHEDULED_LABEL,
  postScheduleState,
} from './scheduleState';

/** The engagement metrics we surface, in reading order across the 2x2 grid. */
const METRIC_CELLS: {
  key: 'views' | 'likes' | 'comments' | 'shares';
  label: string;
  icon: LucideIcon;
}[] = [
  { key: 'views', label: 'Views', icon: Eye },
  { key: 'likes', label: 'Likes', icon: Heart },
  { key: 'comments', label: 'Comments', icon: MessageCircle },
  { key: 'shares', label: 'Shares', icon: Share2 },
];

/**
 * An eBay-style tile for the Posts grid: a large media thumbnail on top (the image, or a
 * kind-appropriate placeholder for video/audio/text), then the title, platform + status, and
 * scheduling info. The whole tile opens the post in the composer.
 */
export function PostTile({
  post,
  badgeState,
  onOpen,
  menu,
}: {
  post: Post;
  badgeState: PostDisplayState;
  onOpen: () => void;
  /**
   * The ⋯ actions menu, rendered by the caller so this component stays presentational. Optional,
   * but omitting it means Tile view offers NO per-post actions at all — which is how Design
   * bundle, Duplicate and Move were unreachable in this layout.
   */
  menu?: ReactNode;
}) {
  const [errored, setErrored] = useState(false);
  // Proxied asset URLs (/api/assets/:id/image) need an authed fetch → object URL; a raw <img src>
  // 401s and would always fall back to the placeholder. External URLs pass through.
  const src = useThumbnailSrc(post.thumbnailUrl);
  // Clear a stale load failure when the resolved image changes (fresh SAS URL / new post).
  useEffect(() => setErrored(false), [src]);
  const showImage = src && !errored;
  const PlaceholderIcon =
    post.mediaKind === ContentKind.DOCUMENT
      ? GalleryHorizontal
      : post.mediaKind === ContentKind.VIDEO
        ? Film
      : post.mediaKind === ContentKind.AUDIO
        ? Music
        : post.mediaKind === ContentKind.IMAGE
          ? ImageIcon
          : FileText;
  const schedule = postScheduleState(post);
  const when =
    schedule.kind === 'published'
      ? `Published ${formatDateTime(schedule.at)}`
      : schedule.kind === 'scheduled'
        ? `${schedule.queued ? 'Queued' : 'Scheduled'} · ${formatDateTime(schedule.at)}`
        : schedule.kind === 'on-approval'
          ? ON_APPROVAL_LABEL
          : schedule.kind === 'reschedule-required'
            ? RESCHEDULE_LABEL
            : UNSCHEDULED_LABEL;
  const platformMeta = post.platform ? PLATFORM_META[post.platform] : null;
  const platformLabel = platformMeta?.label ?? 'Post';
  // publishedAt, NOT the badge: in CAMPAIGN mode the badge is the owning CAMPAIGN's state, so a
  // draft in a published campaign would otherwise get an engagement grid for a post that never
  // went out — and, having no metrics, one that shimmered forever.
  const isPublished = post.publishedAt != null;
  // Three states, and only the middle one shimmers:
  //   metrics == null            → the post can never be measured (no platform id) → dashes
  //   metrics.fetchedAt == null  → measurable, not asked yet                       → shimmer
  //   metrics.fetchedAt != null  → answered; a null field means "not reported"     → dash
  const pending = metricsPending(post);

  return (
    // The tile itself is a button, so the menu cannot live inside it — nesting a button in a
    // button is invalid and the click would open the composer instead. It overlays instead.
    <div className="relative">
      {menu && (
        <div className="absolute right-2 top-2 z-20 rounded-md bg-card/90 shadow-sm backdrop-blur">
          {menu}
        </div>
      )}
      <button
        type="button"
        onClick={onOpen}
        className="focus-ring group flex w-full flex-col overflow-hidden rounded-xl border border-line bg-card text-left shadow-card transition hover:shadow-panel"
      >
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-chip">
        {showImage ? (
          <img
            src={src as string}
            alt=""
            loading="lazy"
            onError={() => setErrored(true)}
            // bg-white on the image itself: it shows only through transparent pixels, so a
            // dark-on-transparent upload stays legible on the dark theme's tile.
            className="h-full w-full bg-white object-cover transition group-hover:scale-[1.02]"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-ink-ghost">
            <PlaceholderIcon className="h-10 w-10" aria-hidden />
          </div>
        )}
        {/*
          A solid card-coloured disc behind the logo (white, or the dark card in the dark theme),
          with the glyph inset so the ring shows all round: the cover image is arbitrary, so every
          mark needs its own background to stay legible on light AND dark photos. The glyph is the
          platform's `ink` class on its own, never parsed out of `tile` (whose tint and dark
          variants are for PlatformIcon): X and Threads draw in `text-ink`, which flips with the
          theme exactly as the disc does, so their mark never vanishes into it. The shadow +
          hairline ring keep the disc itself visible against a same-coloured or blown-out cover.
          Rendered here rather than via PlatformIcon because that component's tinted ROUNDED-SQUARE
          tile is shared with the list/table/calendar rows, where it sits on a plain surface and
          needs none of this.
        */}
        <span
          className="absolute left-2 top-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-card shadow-md ring-1 ring-ink/5"
          title={platformLabel}
          aria-label={platformLabel}
        >
          {platformMeta ? (
            <platformMeta.Icon className={cn('h-3.5 w-3.5', platformMeta.ink)} />
          ) : (
            <Megaphone className="h-3.5 w-3.5 text-ink-faint" aria-hidden />
          )}
        </span>
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-2 p-3">
        <p className="line-clamp-2 min-h-[2.5rem] text-sm font-semibold text-ink">
          {post.title || 'Untitled post'}
        </p>
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge tone={displayStateTone(badgeState)} size="sm">
            {displayStateLabel(badgeState)}
          </Badge>
          {post.campaignName && (
            <span className="inline-flex items-center gap-1 text-xs text-ink-faint">
              <Layers className="h-3 w-3" aria-hidden />
              <span className="max-w-[10rem] truncate">{post.campaignName}</span>
            </span>
          )}
        </div>
        <p className={cn('text-xs', post.publishedAt ? 'text-success dark:text-success-dark' : 'text-ink-muted')}>{when}</p>
        {isPublished && (
          <div className="grid grid-cols-2 gap-x-2 gap-y-1.5 border-t border-line-soft pt-2">
            {METRIC_CELLS.map(({ key, label, icon }) => (
              <MetricCell
                key={key}
                icon={icon}
                label={label}
                value={post.metrics?.[key] ?? null}
                pending={pending}
              />
            ))}
          </div>
        )}
      </div>
      </button>
    </div>
  );
}

/**
 * One cell of the published-post metric grid: glyph · value · label. The value sits in a slot with
 * a fixed height and a floor on its width so the placeholder and the real number occupy the same
 * box — a card must never reflow when metrics land. Every cell always renders, dash included, so
 * the grids stay aligned across the row of cards.
 */
function MetricCell({
  icon: Icon,
  label,
  value,
  pending,
}: {
  icon: LucideIcon;
  label: string;
  /** Null = the platform doesn't report this metric to us. NOT zero — see formatMetric. */
  value: number | null;
  pending: boolean;
}) {
  return (
    <div className="flex min-w-0 items-center gap-1">
      <Icon className="h-3.5 w-3.5 shrink-0 text-ink-faint" aria-hidden />
      <span className="flex h-4 min-w-[1.75rem] shrink-0 items-center">
        {pending ? (
          <>
            <span className="h-2.5 w-7 animate-pulse rounded bg-chip" aria-hidden />
            <span className="sr-only">Pending</span>
          </>
        ) : (
          <span
            // The dash is muted and unweighted so a withheld metric never reads as a real number.
            className={cn(
              'text-xs tabular-nums',
              value == null ? 'text-ink-ghost' : 'font-semibold text-ink',
            )}
            title={value == null ? `${label} aren't reported for this post` : undefined}
          >
            {formatMetric(value)}
          </span>
        )}
      </span>
      <span className="truncate text-[0.6875rem] text-ink-faint">{label}</span>
    </div>
  );
}
